import React, { useState, useEffect, useRef, useCallback } from "react";
import { SearchIcon, XIcon } from "@heroicons/react/solid";
import useGetColleagues from "../../hooks/chats/useGetColleagues";
import PrimaryLoader from "../../components/common/PrimaryLoader";
import InlineLoader from "../../components/loader/InlineLoader";
import Button from "../../components/ui/Button";

const ChatListModal = ({ open, onClose, onSelectContact }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isVisible, setIsVisible] = useState(false);
  const contactsEndRef = useRef(null);

  // Use the custom hook to get colleagues data
  const {
    colleagues,
    loading,
    isFetching,
    updateSearchQuery,
    loadMoreData,
    pagination,
  } = useGetColleagues();

  // Implement infinite scroll using Intersection Observer
  const observer = useRef(null);
  const lastColleagueRef = useCallback(
    (node) => {
      if (loading) return;

      // Disconnect the previous observer if any
      if (observer.current) {
        observer.current.disconnect();
      }

      // Create a new observer
      observer.current = new IntersectionObserver((entries) => {
        // If the last item is visible and we have more pages
        if (entries[0].isIntersecting && pagination?.hasMorePages) {
          loadMoreData();
        }
      });

      // Observe the last item
      if (node) {
        observer.current.observe(node);
      }
    },
    [loading, loadMoreData, pagination?.hasMorePages]
  );

  useEffect(() => {
    if (open) {
      setIsVisible(true);
      // Lock body scroll
      document.body.style.overflow = "hidden";
    } else {
      // Animation out
      setTimeout(() => {
        setIsVisible(false);
        // Restore body scroll
        document.body.style.overflow = "";
      }, 200);
    }

    return () => {
      document.body.style.overflow = "";
      // Clean up observer
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, [open]);

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    updateSearchQuery(event.target.value);
  };

  const handleContactSelect = (colleague) => {
    // Pass the colleague object directly to the parent component
    onSelectContact(colleague);
  };

  if (!open && !isVisible) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 transition-opacity duration-200 ${
        open ? "opacity-100" : "opacity-0"
      }`}
      onClick={onClose}
    >
      <div
        className={`w-full max-w-md max-h-[80vh] bg-white rounded-lg shadow-xl flex flex-col overflow-hidden transform transition-transform duration-200 ${
          open ? "scale-100" : "scale-95"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="bg-primary text-primary-foreground p-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold">New Chat</h2>
          <Button
            onClick={onClose}
            variant={"ghost"}
            size={"icon"}
            className="rounded-full hover:bg-primary-foreground text-red hover:text-primary transition-colors"
          >
            <XIcon className="w-5 h-5" />
          </Button>
        </div>

        {/* Search Box */}
        <div className="p-4">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <SearchIcon className="w-5 h-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search Colleagues"
              value={searchQuery}
              onChange={handleSearchChange}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>

        <div className="border-t border-gray-200"></div>

        {/* Contact List */}
        <div className="overflow-auto flex-1" ref={contactsEndRef}>
          {loading || isFetching ? (
            <div className="p-4 flex justify-center items-center">
              <PrimaryLoader />
            </div>
          ) : colleagues.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              No colleagues found
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {colleagues.map((colleague, index) => {
                // Create initials from first_name and last_name
                const initials =
                  colleague.first_name?.charAt(0).toUpperCase() +
                  colleague.last_name?.charAt(0).toUpperCase();

                // Add ref to the last item for infinite scrolling
                const isLastItem = index === colleagues.length - 1;

                return (
                  <li
                    key={colleague.company_employee_id || colleague.id}
                    onClick={() => handleContactSelect(colleague)}
                    className="hover:bg-gray-100 cursor-pointer transition-colors"
                    ref={isLastItem ? lastColleagueRef : null}
                  >
                    <div className="flex items-center p-4">
                      {colleague.image_path ? (
                        <img
                          src={colleague.image_path}
                          alt={colleague.name}
                          className="w-12 h-12 rounded-full mr-4 object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full mr-4 text-xl bg-gray-700 text-white flex items-center justify-center font-medium">
                          {initials}
                        </div>
                      )}
                      <div>
                        <p className="font-medium">{colleague.name}</p>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {/* Loading indicator at the bottom only for infinite scroll, not initial loading */}
          {!loading && isFetching && (
            <div className="py-4 flex justify-center">
              <InlineLoader />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatListModal;
